import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { AtSign } from "lucide-react";
import { api } from "../lib/api";

/**
 * Textarea / input with built-in @-mention autocomplete.
 *
 * When the user types `@` followed by 1+ characters, an absolutely-positioned
 * dropdown of matching usernames opens beneath the caret. Arrow keys cycle
 * through results; Enter inserts the chosen handle. Tab + Escape dismiss.
 *
 * Props:
 *   - value, onChange (controlled string)
 *   - placeholder, rows, maxLength, className, dataTestId
 *   - onSubmit (called when user hits Enter without a mention dropdown open
 *     AND `submitOnEnter` is true)
 */
const MentionInput = forwardRef(function MentionInput(
  { value, onChange, placeholder, rows = 3, maxLength = 2000, className = "", dataTestId, submitOnEnter = false, onSubmit, multiline = true, ...rest },
  ref,
) {
  const tRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  // Last search to dedup
  const lastQRef = useRef("");

  useImperativeHandle(ref, () => ({
    focus: () => tRef.current?.focus(),
    blur: () => tRef.current?.blur(),
    element: tRef.current,
  }));

  // Detect @-mention by scanning backwards from the caret
  const detectMention = () => {
    const el = tRef.current;
    if (!el) return null;
    const pos = el.selectionStart || 0;
    const before = (value || "").slice(0, pos);
    // Match @handle where handle is the last token (no whitespace inside)
    const m = before.match(/(^|\s)@([A-Za-z0-9_]{1,30})$/);
    if (!m) return null;
    return { query: m[2], start: pos - m[2].length - 1 };
  };

  // Fetch matches when query changes
  useEffect(() => {
    if (!open || !query) {
      setResults([]);
      return;
    }
    if (lastQRef.current === query) return;
    lastQRef.current = query;
    let cancelled = false;
    api.get("/users/search", { params: { q: query, limit: 6 } })
      .then((r) => {
        if (cancelled) return;
        setResults(r.data?.items || []);
        setActive(0);
      })
      .catch(() => setResults([]));
    return () => { cancelled = true; };
  }, [query, open]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(maxLength ? v.slice(0, maxLength) : v);
    const det = detectMention();
    if (det) {
      setOpen(true);
      setQuery(det.query);
    } else {
      setOpen(false);
      setQuery("");
    }
  };

  const insertMention = (username) => {
    const el = tRef.current;
    if (!el) return;
    const pos = el.selectionStart || 0;
    const before = (value || "").slice(0, pos);
    const after = (value || "").slice(pos);
    const replaced = before.replace(/@([A-Za-z0-9_]{1,30})$/, `@${username} `);
    const newVal = replaced + after;
    onChange(newVal);
    setOpen(false);
    setQuery("");
    // restore caret right after the inserted mention
    requestAnimationFrame(() => {
      const newPos = replaced.length;
      el.focus();
      el.setSelectionRange(newPos, newPos);
    });
  };

  const onKeyDown = (e) => {
    if (open && results.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (i + 1) % results.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (i - 1 + results.length) % results.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(results[active].username);
        return;
      }
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
    }
    if (submitOnEnter && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  const Tag = multiline ? "textarea" : "input";
  const tagProps = multiline ? { rows } : { type: "text" };

  return (
    <div className="relative">
      <Tag
        {...tagProps}
        ref={tRef}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        data-testid={dataTestId}
        className={className}
        {...rest}
      />
      {open && results.length > 0 && (
        <div
          data-testid="mention-autocomplete"
          className="absolute z-30 left-0 mt-1 min-w-[200px] bg-[#0a0a0a] border border-[#a78bfa]/60 shadow-xl shadow-black/60"
        >
          {results.map((u, idx) => (
            <button
              type="button"
              key={u.username}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(u.username);
              }}
              data-testid={`mention-suggest-${u.username}`}
              className={`w-full text-left px-3 py-2 font-mono text-xs tracking-[0.1em] flex items-center gap-2 transition-colors ${
                idx === active
                  ? "bg-[#a78bfa]/[0.12] text-white"
                  : "text-zinc-300 hover:bg-[#a78bfa]/[0.08]"
              }`}
            >
              <AtSign size={11} className="text-[#a78bfa]" />
              <span>{u.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default MentionInput;
